import { Typography } from '@mui/material';
import { DataGridProProps, GridColDef } from '@mui/x-data-grid-pro';

import { LineItemType } from 'common/src/types/enums/LineItemType.enum';
import { DemandUnit, UsageUnit } from 'common/src/types/enums/Service.enum';

import DataTable from 'portal/src/components/common/DataTable';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { numberFormatter } from 'portal/src/utils/numberFormatter';

import allocationTableStyle from './allocationTableStyle';

import { useBillFormContext } from '../BillFormContext';

type AllocationAccountRow = {
  id: string;
  path: string[];
  accountId?: string;
  meterId?: string;
  startDate?: string;
  endDate?: string;
  subTypeId?: string;
  type?: LineItemType;
  unit?: UsageUnit | DemandUnit;
  usage?: number;
  cost?: number;
};

const AllocationAccounts = () => {
  const { allocations, allocationMeters, lineItemSubtypes, subAccounts } =
    useBillFormContext();

  const columns: GridColDef<AllocationAccountRow>[] = [
    {
      field: 'meterId',
      headerName: 'Meter',
      flex: 1,
      valueGetter: (value: string) =>
        allocationMeters.data.find((m) => m.id === value)?.name,
    },
    {
      field: 'facility',
      headerName: 'Facility Name',
      flex: 0.5,
      valueGetter: (_, row) =>
        allocationMeters.data.find((m) => m.id === row.meterId)?.parentFacility
          ?.name,
    },
    {
      field: 'startDate',
      ...addDateColumnProps('Start Date'),
    },
    {
      field: 'endDate',
      ...addDateColumnProps('End Date'),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.2,
    },
    {
      field: 'subTypeId',
      headerName: 'Sub Type',
      flex: 0.5,
      valueFormatter: (value: string) =>
        lineItemSubtypes.data.find(({ id }) => id === value)?.name ?? '',
    },
    {
      field: 'unitOfMeasure',
      headerName: 'Unit',
      width: 96,
    },
    {
      field: 'usage',
      headerName: 'Usage',
      width: 96,
      valueFormatter: (value: number) =>
        numberFormatter(value, { style: 'decimal' }),
    },
    {
      field: 'cost',
      headerName: 'Cost',
      renderCell: ({ row }) => {
        const cost = numberFormatter(row.cost);

        if (row.accountId) {
          return (
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {cost}
            </Typography>
          );
        }

        return cost;
      },
    },
  ];

  const gridRows: AllocationAccountRow[] = allocations?.reduce((rows, a) => {
    const accountRow = {
      id: a.accountId,
      path: [a.accountId],
      accountId: a.accountId,
      cost: a.amount,
    };

    rows.push(accountRow);

    rows.push(
      ...a.lineItems.map((li) => {
        return {
          path: [a.accountId, li.id],
          ...li,
        };
      }),
    );

    return rows;
  }, []);

  const getTreeDataPath: DataGridProProps['getTreeDataPath'] = (
    row: AllocationAccountRow,
  ): string[] => row.path;

  return (
    <DataTable
      autoHeight
      checkboxSelection={false}
      columns={columns}
      defaultGroupingExpansionDepth={-1}
      getRowClassName={({ row }) =>
        row.accountId ? 'striped-even' : 'striped-odd'
      }
      getTreeDataPath={getTreeDataPath}
      groupingColDef={{
        flex: 1,
        headerName: 'Account Number',
        valueGetter: (_, row) =>
          subAccounts.find((acc) => acc.id === row.accountId)?.number ?? '',
      }}
      hideFooter
      isRowNavigation={false}
      rows={gridRows}
      sx={allocationTableStyle}
      treeData
    />
  );
};

export default AllocationAccounts;
